<template>
  <v-card class="ma-0 pa-0 white pt-2">
    <div class="d-flex justify-space-between px-4">
      <div style="min-width: 265px">
        <v-autocomplete
          class="mt-3 white"
          dense
          hide-details
          item-text="text"
          item-value="value"
          outlined
          :items="availableReports"
          :label="$t('insights.report_one')"
          :value="selectedReport"
          @change="handleReportChange"
        />
      </div>

      <DateRangeFilter
        class="mt-3"
        :allowedDates="dateSelectorProperties.allowedDates"
        :dateValues="dateSelectorProperties.dates"
        :id="dateSelectorProperties.key"
        :label="dateSelectorProperties.name"
        :options="dateSelectorProperties.options"
        :value="dateSelectorProperties.value"
        @change="handleChangeDates"
      />
    </div>

    <v-divider class="my-4" />

    <div class="d-flex justify-space-between">
      <div class="d-flex flex-wrap px-4 justify-start">
        <template v-for="(filter, index) in renderInputFilters">
          <div
            v-if="showFilter(filter)"
            class="mb-4"
            style="min-width: 265px; max-width: 600px"
            :key="filter"
            :class="{
              'me-2': index !== renderInputFilters.length - 1,
            }"
          >
            <RoutineDefinitionsFilter
              v-if="filter === 'routines'"
              :selectedRoutineIds="selectedFilters.routines || []"
              @select="handleSelectRoutine"
            />

            <TeamsFilter
              v-else-if="filter === 'team'"
              :selectedTeamId="selectedFilters.team"
              @select="handleSelectTeam"
            />

            <Autocomplete
              v-else
              :id="filter"
              :customLabel="getFilterLabel(filter)"
              :filterLabelsKeys="filterLabelsKeys"
              :helperText="getFilterHelper(filter)"
              :loading="loadingByType[filter]"
              :removable="canRemoveFilter(filter)"
              :multiple="isMultipleSelection(filter)"
              :noFilter="isRemoteSearch(filter)"
              :options="optionsByType[filter]"
              :value="selectedFilters[filter]"
              @search="handleSearch($event, filter)"
              @change="handleFilterChange($event, filter)"
              @delete="handleFilterDelete(filter)"
            />
          </div>
        </template>

        <FiltersMenu
          v-if="menuFilters.length"
          :options="menuFilters"
          @click="handleAddFilter"
        >
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              class="blue-grey lighten-3 mt-1 ms-3"
              elevation="0"
              x-small
            >
              <v-icon color="blue-grey">mdi-plus</v-icon>
            </v-btn>
          </template>
        </FiltersMenu>
      </div>
      <div class="me-4 d-flex">
        <v-btn
          color="blue"
          text
          class="me-2"
          rounded
          :disabled="loading"
          @click="resetFilters"
          >{{ $t('insights.clear_filters') }}</v-btn
        >
        <v-btn
          color="blue"
          class="white--text elevation-0"
          rounded
          :disabled="loading || !dates[1]"
          @click="fetchReport"
          >{{ $t('insights.apply_filters') }}</v-btn
        >
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import _ from 'lodash'
import * as moment from 'moment'
import { utils } from 'frontend-js-shared'

import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import FiltersMenu from '@/components/UI/Menu.vue'
import DateRangeFilter from '@/components/Filters/DateRangeFilter.vue'
import RoutineDefinitionsFilter from './RoutineDefinitionsFilter.vue'
import TeamsFilter from './TeamsFilter.vue'
import Autocomplete from './Autocomplete.vue'

const DEFAULT_REPORT_TYPE = 'overview'

const REMOTE_SEARCH_FILTERS = ['challenge', 'routines']

const FILTER_TYPES = [
  'countryCodes',
  'sites',
  'siteGroups',
  'tagGroups',
  'tags',
  'challenge',
  'team',
  'routines',
  'routineType',
  'date',
]

const FILTERS_MULTIPLE_SELECTIONS = [
  'siteGroups',
  'countryCodes',
  'sites',
  'tagGroups',
  'tags',
  'routines',
]

const DEFAULT_PRIMARY_FILTERS = ['sites', 'tags']

const PRIMARY_FILTERS_BY_REPORT = {
  overview: ['sites', 'tags'],
  validated_impacts: ['sites', 'tags'],
  non_validated_impacts: ['sites', 'tags'],
  routines_overview: ['siteGroups', 'routineType'],
  routines: ['sites', 'routines'],
  quick_action: ['sites', 'tags'],
  improvement_idea: ['sites', 'tags'],
  problem_solving: ['sites', 'tags'],
  tasks: ['sites', 'tags'],
  users: ['sites', 'tags'],
  global: ['siteGroups'],
  teams: ['team', 'sites'],
  challenges: ['sites'],
}
const REPORTS_SUPPORTING_FUTURE_DATES = ['validated_impacts']
const PREDEFINED_DATE_RANGES = utils.dates.PREDEFINED_DATE_RANGES

export default {
  name: 'Filters',

  mixins: [mixin, trackerMixin],

  components: {
    Autocomplete,
    DateRangeFilter,
    FiltersMenu,
    RoutineDefinitionsFilter,
    TeamsFilter,
  },

  props: {
    loading: Boolean,
    reportsList: {
      type: Array,
      required: true,
    },
    currentOrganizationId: {
      type: String,
      required: true,
    },
    filtersByReport: {
      type: Object,
      required: true,
    },
    account: {
      type: Object,
      required: true,
    },
    initialFilters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      dates: [],
      selectedFiltersToShow: [],
      selectedReport: DEFAULT_REPORT_TYPE,
      selectedFilters: {},
      countryCodes: [],
      routines: [],
      challenges: [],
      teamsCatalogMap: null,
      loadingByType: {},
      filterLabelsKeys: {
        countryCodes: 'country_other',
        sites: 'site_other',
        siteGroups: 'site_group_other',
        tags: 'tag_other',
        tagGroups: 'tag_group_other',
        challenge: 'challenge_other',
        team: 'team_one',
        routines: 'routine_one',
        routineType: 'routine_type',
      },
      today: moment().format('YYYY-MM-DD'),
      organizationStructure: {},
      dateOptionSelected:
        this.$route.query['dateOption'] ??
        this.$constants.DATE_FILTER_OPTIONS.today,
      cacheByType: {
        routines: {},
      },
    }
  },

  async mounted() {
    if (this.currentOrganizationId !== this.organizationId)
      this.organizationStructure = await this.getOrganizationStructure()

    this.setInitialFilters()

    this.fetchReport()
  },

  watch: {
    renderInputFilters: {
      immediate: true,
      handler(newFilters, oldFilters) {
        const difference = _.difference(newFilters, oldFilters)
        if (
          difference.includes('countryCodes') &&
          _.isEmpty(this.optionsByType.countryCodes)
        ) {
          this.getCountryCodes()
        }
        if (
          newFilters.includes('challenge') &&
          _.isEmpty(this.optionsByType.challenge)
        ) {
          this.getChallenges()
        }
      },
    },

    searchTerm: {
      deep: true,
      handler: _.debounce(function(search) {
        if (search.challenge) {
          this.getChallenges(search.challenge)
        }
      }, 500),
    },

    selectedReport(newReport) {
      if (newReport === 'teams') this.fetchTeamsCatalog()
    },

    async initialFilters() {
      await this.$nextTick()
      this.setInitialFilters()
    },
  },

  computed: {
    ...mapGetters([
      'organizationSiteGroups',
      'organizationSites',
      'organizationTags',
      'organizationTagGroups',
      'organizationId',
      'accountId',
    ]),

    appliedFiltersForTracking() {
      const filledFilters = {}
      for (const filter in this.sanitizedFilters) {
        filledFilters[filter] = _.isArray(this.sanitizedFilters[filter])
          ? this.sanitizedFilters[filter].length
          : true
      }

      return filledFilters
    },

    sanitizedFilters() {
      const sanitizedFilters = {}

      for (const filter in this.selectedFiltersWithDate) {
        const isDateFilter = filter === 'startDate' || filter === 'endDate'
        const filterIsIncluded = this.filtersList.includes(filter)

        if (isDateFilter || filterIsIncluded) {
          sanitizedFilters[filter] = this.selectedFiltersWithDate[filter]
        }
      }

      return sanitizedFilters
    },

    allowFutureDates() {
      return REPORTS_SUPPORTING_FUTURE_DATES.includes(this.selectedReport)
    },

    siteGroups() {
      return this.currentOrganizationId === this.organizationId
        ? this.organizationSiteGroups
        : this.organizationStructure?.siteGroups ?? []
    },

    sites() {
      return this.currentOrganizationId === this.organizationId
        ? this.organizationSites
        : this.organizationStructure?.sites ?? []
    },

    sitesMap() {
      return new Map(this.sites.map(site => [site.id, site]))
    },

    siteOptions() {
      return _.orderBy(
        this.sites.map(site => {
          return {
            ...site,
            text: site.name,
            value: site.id,
            subtitle: this.$t(`countries.${site.countryCodes[0]}`),
            subtitleIcon: 'mdi-earth',
            secondText:
              site.id === utils.getEntityId(this.account.site)
                ? this.$t('common.my_site')
                : null,
          }
        }),
        ['name'],
        ['asc']
      )
    },

    tags() {
      return this.currentOrganizationId === this.organizationId
        ? this.organizationTags
        : this.organizationStructure?.tags ?? []
    },

    tagGroups() {
      return this.currentOrganizationId === this.organizationId
        ? this.organizationTagGroups
        : this.organizationStructure?.tagGroups ?? []
    },

    availableRoutines() {
      const selectedRoutineIds = this.selectedFilters?.routines ?? []
      const cachedRoutines = this.cacheByType?.routines ?? {}

      return _.uniqBy(
        [...selectedRoutineIds.map(id => cachedRoutines[id]), ...this.routines],
        'id'
      )
    },

    optionsByType() {
      return {
        siteGroups: this.siteGroups?.map(group => {
          return { text: group.name, value: group.id }
        }),

        sites: this.siteOptions,
        tagGroups: this.tagGroups?.map(group => {
          return { text: group.name, value: group.id }
        }),
        tags: this.tags?.map(tag => {
          return { text: tag.name, value: tag.id }
        }),
        countryCodes: this.countries.filter(country =>
          this.countryCodes.includes(country.value)
        ),
        routines: [],
        routineType: this.routineTypes,
        challenge: this.challenges?.map?.(challenge => {
          return { text: challenge.name, value: challenge.id }
        }),
      }
    },

    routineTypes() {
      const types = _.chain(this.$constants.routines.types)
        .map(type => ({
          value: type.value,
          text: this.$t(type.name),
        }))
        .orderBy('text', 'asc')
        .value()
      return types
    },

    countries() {
      return this.$constants?.countries?.map(country => {
        return {
          ...country,
          value: country.code,
          text: this.$t(`countries.${country.code}`),
        }
      })
    },

    selectedTeamIsGlobal() {
      if (this.selectedReport !== 'teams') return false

      return (
        this.teamsCatalogMap?.get?.(this.selectedFilters?.team)?.site === null
      )
    },

    filtersList() {
      const filters =
        this.filtersByReport[this.selectedReport]?.filter?.(filterType =>
          FILTER_TYPES?.includes?.(filterType)
        ) ?? []

      if (filters.includes('tags') && !filters.includes('tagGroups')) {
        filters.push('tagGroups')
      }

      if (filters.includes('routines')) {
        // Invert position of filters when routines is selected
        return ['date', 'routines', 'sites']
      }

      if (filters.includes('team')) {
        return this.selectedTeamIsGlobal
          ? ['date', 'team', 'sites'] // Report service returns 'team' and 'sites' filters in an inverted position, so we have to change positions manually. Also, the sites filter is only shown for Global teams.
          : ['date', 'team']
      }

      return filters ?? []
    },

    renderInputFilters() {
      const snakeCaseReport = _.snakeCase(this.selectedReport)

      const primaryFilters = this.filtersList.filter(filter =>
        PRIMARY_FILTERS_BY_REPORT[snakeCaseReport].includes(filter)
      )

      primaryFilters.push(...this.selectedFiltersToShow)

      return _.uniq(primaryFilters)
    },

    menuFilters() {
      const menuFilters = this.filtersList
        .filter(
          filter =>
            !this.renderInputFilters.includes(filter) && filter !== 'date'
        )
        .map(option => {
          return { title: this.getFilterLabel(option), id: option }
        })

      return menuFilters
    },

    selectedFiltersWithDate() {
      const isDatesInFilters = this.filtersList.includes('date')
      const dates = {
        startDate: this.dates[0],
        endDate: this.dates[1],
      }

      return {
        ...(isDatesInFilters && dates),
        ...this.selectedFilters,
      }
    },

    availableReports() {
      return this.reportsList?.map(reportOption => {
        return {
          value: reportOption.type,
          text: reportOption.name,
        }
      })
    },

    areSelectedFiltersCompatible() {
      if (
        this.selectedFiltersWithDate?.sites?.length &&
        this.selectedFiltersWithDate?.countryCodes?.length
      ) {
        return this.selectedSitesAndCountriesAreCompatible
      }
      if (
        this.selectedFiltersWithDate?.sites?.length &&
        this.selectedFiltersWithDate?.siteGroups?.length
      ) {
        return this.selectedSitesAndSiteGroupsAreCompatible
      }

      if (
        this.selectedFiltersWithDate?.tags?.length &&
        this.selectedFiltersWithDate?.tagGroups?.length
      ) {
        return this.selectedTagsAndTagGroupsAreCompatible
      }

      return true
    },

    selectedSitesAndCountriesAreCompatible() {
      const selectedSitesCountryCodes = _.uniq(
        this.sites
          .filter(site =>
            this.selectedFiltersWithDate?.sites?.some(
              siteId => site.id === siteId
            )
          )
          ?.flatMap(site => site.countryCodes)
      )

      return this.selectedFiltersWithDate?.countryCodes?.some?.(countryCode =>
        selectedSitesCountryCodes?.includes?.(countryCode)
      )
    },

    selectedSitesAndSiteGroupsAreCompatible() {
      const selectedSitesSiteGroups = _.uniq(
        this.sites
          .filter(site =>
            this.selectedFiltersWithDate?.sites?.some(
              siteId => site.id === siteId
            )
          )
          ?.flatMap(site => site.groups)
      )

      return this.selectedFiltersWithDate?.siteGroups?.some?.(siteGroup =>
        selectedSitesSiteGroups?.includes?.(siteGroup)
      )
    },

    selectedTagsAndTagGroupsAreCompatible() {
      const selectedTagsTagGroups = _.uniq(
        this.tags
          .filter(tag =>
            this.selectedFiltersWithDate?.tags?.some(tagId => tag.id === tagId)
          )
          ?.flatMap(tag => tag.groups)
      )

      return this.selectedFiltersWithDate?.tagGroups?.some?.(tagGroup =>
        selectedTagsTagGroups?.includes?.(tagGroup)
      )
    },

    dateOptions() {
      return [
        {
          value: PREDEFINED_DATE_RANGES.today,
          text: this.$t('explore.filters.today'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.today),
        },
        {
          value: PREDEFINED_DATE_RANGES.yesterday,
          text: this.$t('explore.filters.yesterday'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.yesterday),
        },
        {
          value: PREDEFINED_DATE_RANGES.thisMonth,
          text: this.$t('explore.filters.this_month'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.thisMonth),
        },
        {
          value: PREDEFINED_DATE_RANGES.thisYear,
          text: this.$t('explore.filters.this_year'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.thisYear),
        },
        {
          value: PREDEFINED_DATE_RANGES.lastMonth,
          text: this.$t('explore.filters.last_month'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.lastMonth),
        },
        {
          value: PREDEFINED_DATE_RANGES.last7Days,
          text: this.$t('explore.filters.last_7_days'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.last7Days),
        },
        {
          value: PREDEFINED_DATE_RANGES.last30Days,
          text: this.$t('explore.filters.last_30_days'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.last30Days),
        },
        {
          value: PREDEFINED_DATE_RANGES.last90Days,
          text: this.$t('explore.filters.last_90_days'),
          dates: this.getDateRangeForOption(PREDEFINED_DATE_RANGES.last90Days),
        },
        {
          value: this.$constants.DATE_FILTER_OPTIONS.custom,
          text: this.$t('explore.filters.custom'),
        },
      ]
    },

    requireDateSelector() {
      return (
        this.dateOptionSelected === this.$constants.DATE_FILTER_OPTIONS.custom
      )
    },

    dateSelectorProperties() {
      return {
        allowedDates: this.allowFutureDates ? null : this.allowDatesUntilNow,
        options: this.dateOptions,
        key: 'date',
        value: this.dateOptionSelected,
        name: this.$t('common.date.date'),
        dates: this.dates,
      }
    },
  },

  methods: {
    ...mapMutations({
      updateActiveReport: 'report/updateActiveReport',
    }),

    handleSearch(terms, filter) {
      if (!this.isRemoteSearch(filter) || !terms) return

      if (filter === 'challenge') {
        this.searchChallenges(terms)
      }
    },

    searchChallenges: _.debounce(function(terms) {
      this.getChallenges(terms)
    }, 500),

    handleFilterChange(selection, filter) {
      this.selectedFilters = { ...this.selectedFilters, [filter]: selection }
    },

    handleFilterDelete(filter) {
      const filterIndex = this.selectedFiltersToShow.indexOf(filter)
      this.selectedFiltersToShow.splice(filterIndex, 1)

      if (this.selectedFilters[filter]) {
        this.selectedFilters[filter] = null
      }
    },

    allowDatesUntilNow(date) {
      return date <= moment().format('YYYY-MM-DD')
    },

    isRemoteSearch(filter) {
      return REMOTE_SEARCH_FILTERS.includes(filter)
    },

    async getOrganizationStructure() {
      const response = await api.organization.getOrganizationStructure(
        this.currentOrganizationId
      )

      return response.data
    },

    handleChangeDates(event) {
      this.dateOptionSelected = event.value
      this.dates = event.dates
    },

    setDates(dates) {
      this.dates = [...dates]
    },

    async getCountryCodes() {
      try {
        this.loadingByType.countryCodes = true
        const response = await api.country.getAllCountryCodes(
          this.currentOrganizationId
        )

        this.countryCodes = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingByType.countryCodes = false
      }
    },

    async fetchTeamsCatalog() {
      const { data: teamsCatalog } = await api.teams.getTeamsCatalog(
        this.currentOrganizationId
      )
      this.teamsCatalogMap = new Map(teamsCatalog.map(team => [team.id, team]))
    },

    handleSelectRoutine(routines) {
      this.selectedFilters = {
        ...this.selectedFilters,
        routines,
      }
    },

    handleSelectTeam(team) {
      this.selectedFilters = {
        ...this.selectedFilters,
        team,
      }
    },

    async getChallenges(terms) {
      try {
        this.loadingByType.challenge = true

        const response = await api.explore.getContent({
          organizationId: this.currentOrganizationId,
          terms,
          limit: 50,
          filters: {
            types: [this.$constants.exploreEntityTypes.challenge],
          },
          sort: terms
            ? { by: '_score', order: 'desc' }
            : { by: 'updatedTimestamp', order: 'desc' },
        })

        this.challenges = response.data.results.map(
          result => result.response
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingByType.challenge = false
      }
    },

    canRemoveFilter(filter) {
      const snakeCaseReport = _.snakeCase(this.selectedReport)

      const filtersByReport = PRIMARY_FILTERS_BY_REPORT[snakeCaseReport]

      return filtersByReport.length
        ? !filtersByReport.includes(filter)
        : !DEFAULT_PRIMARY_FILTERS.includes(filter)
    },

    handleAddFilter(filter) {
      this.selectedFiltersToShow.push(filter.id)
    },

    showFilter(filter) {
      return filter !== 'date'
    },

    getFilterLabel(filterName) {
      switch (this.selectedReport) {
        case 'routines':
          return filterName === 'sites'
            ? this.$t('insights.filter_executed_in')
            : _.capitalize(
                this.$t(`common.${this.filterLabelsKeys[filterName]}`)
              )
        case 'teams':
          return filterName === 'sites'
            ? this.$t('insights.filter_activity_in')
            : _.capitalize(
                this.$t(`common.${this.filterLabelsKeys[filterName]}`)
              )
        default:
          return _.capitalize(
            this.$t(`common.${this.filterLabelsKeys[filterName]}`)
          )
      }
    },

    getFilterHelper(filterName) {
      switch (this.selectedReport) {
        case 'teams':
          return filterName === 'sites'
            ? this.$t('insights.filter_helpers.teams_sites')
            : null
        default:
          return null
      }
    },

    isMultipleSelection(filterType) {
      return FILTERS_MULTIPLE_SELECTIONS.includes(filterType)
    },

    getFormattedValues(values) {
      const matchedFilters = {}

      this.filtersList.forEach(filter => {
        if (_.has(values, filter)) {
          const value =
            this.isMultipleSelection(filter) && !_.isArray(value)
              ? _.chain([values[filter]]) // Enforcing array for multiple selection filters
                  .flatten()
                  .uniq()
                  .value()
              : values[filter]

          matchedFilters[filter] = value
        }
      })

      return matchedFilters
    },

    setDefaultValues() {
      if (this.filtersList.includes('team')) this.setDefaultTeam()
      if (this.filtersList.includes('routineType')) this.setDefaultRoutineType()

      // The UX improvement of preselecting the user's site (implemented in the next block of code) will only be applied if there are no selected sites, to avoid overwriting any intentional selection made by the user when changing between reports
      if (!_.isEmpty(this.selectedFilters.sites)) return

      // For a better experience, we set the user's site for any user that is not an organization admin, rever admin or insights admin
      if (
        this.filtersList.includes('sites') &&
        !this.account.isOrganizationAdmin &&
        !this.account.isAdmin &&
        !this.account.isInsightsAdmin
      ) {
        this.selectedFilters.sites = [utils.getEntityId(this.account.site)]
      }
    },

    setInitialFilters() {
      this.selectedReport = this.initialFilters?.type ?? DEFAULT_REPORT_TYPE

      const snakeCaseReport = _.snakeCase(this.selectedReport)
      const filterValues = this.getFormattedValues(this.initialFilters.filters)

      this.setDefaultValues()
      this.setInitialDates()

      this.selectedFilters = {
        ...this.selectedFilters,
        ...filterValues,
      }

      this.selectedFiltersToShow = _.difference(
        Object.keys(this.selectedFilters),
        PRIMARY_FILTERS_BY_REPORT[snakeCaseReport]
      )
    },

    setInitialDates() {
      this.dates = [
        this.initialFilters?.dates?.startDate || this.today,
        this.initialFilters?.dates?.endDate || this.today,
      ]
    },

    setDefaultTeam() {
      const sampleUserTeam = this.account?.teams?.[0] ?? null

      if (sampleUserTeam) {
        this.selectedFilters.team = sampleUserTeam
      }
    },

    setDefaultRoutine() {
      const defaultValue = _.head(this.optionsByType?.routines)?.value
      this.selectedFilters.routines = defaultValue ? [defaultValue] : []
    },

    setDefaultRoutineType() {
      this.selectedFilters.routineType = null
    },

    resetFilters() {
      this.cleanFilters()
      this.dates = [this.today, this.today]
      this.fetchReport()
    },

    trackFilterEvent(selectedReport) {
      const accountSite =
        this.account?.site?._id ?? this.account?.site?.id ?? this.account?.site

      const trackData = {
        selectedFilters: this.appliedFiltersForTracking,
        userSiteMatchWithFilterSite:
          this.sanitizedFilters?.sites?.includes?.(accountSite) ?? false,
        selectedReport,
        isDateRangeSelected:
          this.sanitizedFilters.startDate !== this.sanitizedFilters.endDate,
      }

      this.$track('insights.filter', this.$store, trackData)
    },

    fetchReport() {
      if (!this.areSelectedFiltersCompatible) {
        this.$emit('showWrongFiltersEmptyState')
        return
      }

      const report =
        this.selectedReport ?? this.initialFilters?.type ?? DEFAULT_REPORT_TYPE

      this.$emit('filterSelected', report, this.sanitizedFilters)

      this.updateRouteWithDateOptionSelected()

      this.trackFilterEvent(this.selectedReport)
    },

    async updateRouteWithDateOptionSelected() {
      const newQueryParams = {
        ...this.$route.query,
        dateOption: this.dateOptionSelected,
      }
      await this.$router
        .replace({
          name: 'reports',
          query: newQueryParams,
        })
        .catch(() => {}) // When updating query params a "NavigationDuplicated" exception is thrown. This catch block is to prevent this, but it also could lead to some different exceptions being ignored.
    },

    cleanFilters() {
      const reportFilters = PRIMARY_FILTERS_BY_REPORT[this.selectedReport]
      const newFilters = {}

      if (reportFilters.includes('sites'))
        newFilters.sites = this.selectedFilters.sites
      if (reportFilters.includes('tags'))
        newFilters.tags = this.selectedFilters.tags

      this.selectedFilters = newFilters

      this.setDefaultValues()
    },

    async handleReportChange(report) {
      this.selectedReport = report
      this.updateActiveReport(report)
      this.cleanFilters()
      this.selectedFiltersToShow = []

      if (!this.dates[1]) return

      this.fetchReport()
    },

    containFutureDates(dates = []) {
      const today = utils.dates.formatDate()
      const isFutureStartDate =
        utils.dates.calculateDifferenceInDays(today, dates?.[0]) > 0
      const isFutureEndDate =
        utils.dates.calculateDifferenceInDays(today, dates?.[1]) > 0

      return isFutureStartDate || isFutureEndDate
    },

    getDateRangeForOption(dateRange) {
      const { startDate, endDate } = utils.dates.getPredefinedDateRange(
        dateRange
      )

      return [startDate, endDate]
    },
  },
}
</script>
